/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from "react";

const backendUrl = import.meta.env.VITE_BACKEND_URL;

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  kind?: "normal" | "alert";
}

interface ChatStreamOptions {
  index: string;
  llm_provider?: string;
  sessionId?: string | null;
  initialHistory?: ChatMessage[];
}

interface StreamPayload {
  // El backend envía chunks incrementales con formato: {"delta": "texto"}
  delta?: string | { role?: string; content?: string };
  error?: unknown;
}

interface GuardrailErrorPayload {
  code?: string;
  message?: string;
  reasons?: string[];
  phase?: string;
}

const REASON_LABELS: Record<string, string> = {
  PII_BLOCKED:
    "Tu mensaje incluye datos personales (por ejemplo, correo, RFC o telefono).",
  PII_REDACTED: "Detectamos datos personales sensibles en el contenido.",
  DLP_BLOCKED:
    "Detectamos posible informacion confidencial (por ejemplo, claves o tokens).",
  DLP_REDACTED: "Detectamos informacion confidencial y la ocultamos.",
  MODERATION_BLOCKED: "El contenido no cumple con las politicas de seguridad.",
};

function normalizeReasonCode(reason: string): string {
  const firstToken = reason.trim().split(/\s+/)[0] ?? "";
  return firstToken.replace(/[^A-Za-z0-9_]/g, "").toUpperCase();
}

function toFriendlyReason(reason: string): string {
  const code = normalizeReasonCode(reason);
  return (
    REASON_LABELS[code] ?? "Se detecto contenido sensible en la solicitud."
  );
}

function phaseHint(phase?: string): string {
  if (phase === "input") {
    return "Tu mensaje parece incluir datos sensibles. Elimínalos y vuelve a intentarlo.";
  }
  if (phase === "pre_llm_prompt") {
    return "El contexto de la conversación contiene datos sensibles. Puedes reformular o limpiar el historial.";
  }
  if (phase === "output" || phase === "output_stream") {
    return "La respuesta fue detenida para evitar exposición de datos sensibles.";
  }
  return "La solicitud fue bloqueada por políticas de seguridad.";
}

function isGuardrailErrorPayload(
  value: unknown,
): value is GuardrailErrorPayload {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.code === "string" ||
    typeof obj.message === "string" ||
    Array.isArray(obj.reasons)
  );
}

function buildAlertText(value: unknown): string {
  if (typeof value === "string") {
    return `⚠️ **No pudimos completar la respuesta**\n\n${value}`;
  }

  if (isGuardrailErrorPayload(value)) {
    const title = "⚠️ **Solicitud bloqueada por seguridad**";
    const message = "";
    const hint = value.phase ? `\n\n${phaseHint(value.phase)}` : "";
    const reasons =
      Array.isArray(value.reasons) && value.reasons.length > 0
        ? `\n\n**Motivos detectados:**\n${Array.from(
            new Set(value.reasons.map((reason) => toFriendlyReason(reason))),
          )
            .map((reason) => `- ${reason}`)
            .join("\n")}`
        : "";
    return `${title}\n\n${message}${hint}${reasons}`;
  }

  return "⚠️ **No pudimos completar la respuesta**\n\nOcurrió un error inesperado.";
}

function buildErrorSummary(value: unknown): string {
  if (typeof value === "string") return value;
  if (isGuardrailErrorPayload(value)) {
    const message =
      value.message?.trim() ||
      "La solicitud fue bloqueada por políticas de seguridad.";
    if (!value.phase) return message;
    return `${message} (${phaseHint(value.phase)})`;
  }
  return "Error en el stream";
}

type LoadMode = "replace" | "append";

export function useRetroChatStream(options: ChatStreamOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inProgressIndexRef = useRef<number | null>(null);
  const initialHistoryRef = useRef<ChatMessage[] | undefined>(undefined);

  // Cargar historial inicial cuando cambia o cuando el componente se monta
  useEffect(() => {
    const hasInitialHistory =
      options.initialHistory && options.initialHistory.length > 0;
    const currentHistoryStr = JSON.stringify(initialHistoryRef.current);
    const newHistoryStr = JSON.stringify(options.initialHistory);
    const isDifferentHistory = currentHistoryStr !== newHistoryStr;

    // Si hay historial inicial y es diferente al actual, actualizarlo
    if (hasInitialHistory && isDifferentHistory) {
      // Solo actualizar si no hay una operación en progreso
      if (!loading && inProgressIndexRef.current === null) {
        abortRef.current?.abort();
        abortRef.current = null;
        inProgressIndexRef.current = null;
        setLoading(false);
        setError(null);
        setMessages(options.initialHistory ?? []);
        initialHistoryRef.current = options.initialHistory;
      }
    } else if (!hasInitialHistory) {
      // Si no hay historial inicial, limpiar mensajes solo si no hay operación en progreso
      if (
        !loading &&
        inProgressIndexRef.current === null &&
        messages.length === 0
      ) {
        setMessages([]);
        initialHistoryRef.current = undefined;
      }
    }
  }, [options.initialHistory, loading, messages.length]);

  const loadHistory = useCallback(
    (history: ChatMessage[], mode: LoadMode = "replace") => {
      abortRef.current?.abort();
      abortRef.current = null;
      inProgressIndexRef.current = null;
      setLoading(false);
      setError(null);

      setMessages((prev) =>
        mode === "append" ? [...prev, ...history] : history,
      );
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    inProgressIndexRef.current = null;
    setLoading(false);
  }, []);

  const sendMessage = useCallback(
    async (userText: string, history: ChatMessage[] = [], actividad: number) => {
      setError(null);
      setLoading(true);

      setMessages((prev) => [...prev, { role: "user", content: userText }]);

      let assistantIndex: number | null = null;
      setMessages((prev) => {
        assistantIndex = prev.length;
        inProgressIndexRef.current = assistantIndex!;
        return [...prev, { role: "assistant", content: "" }];
      });

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        // 👇 SELECCIONAR ENDPOINT
        const endpoint = options.sessionId
          ? `${backendUrl}/retro-api/session/query` // CON CONTEXTO
          : `${backendUrl}/retro-api/chat`; // GENÉRICO

        // 👇 CONSTRUIR BODY SEGÚN ENDPOINT
        const requestBody = options.sessionId
          ? {
              session_id: options.sessionId,
              question: userText,
              llm_provider: options.llm_provider ?? "OpenAI",
              index: options.index,
              top_k: 5,
              actividad: actividad,
            }
          : {
              message: userText,
              history,
              llm_provider: options.llm_provider ?? "OpenAI",
              vector_index: options.index,
              actividad: actividad,
            };

        const res = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream", // Ambos endpoints ahora son SSE
          },
          body: JSON.stringify(requestBody),
          credentials: "include",
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        if (!res.body) throw new Error("No stream body");

        // 👇 EL RESTO DEL CÓDIGO DE STREAMING ES IGUAL PARA AMBOS
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let done = false;

        while (!done) {
          const { value, done: streamDone } = await reader.read();
          done = streamDone;
          buffer += value ? decoder.decode(value, { stream: true }) : "";

          let sepIndex: number;
          while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
            const rawEvent = buffer.slice(0, sepIndex);
            buffer = buffer.slice(sepIndex + 2);

            const lines = rawEvent.split("\n").map((l) => l.trimEnd());
            const dataLines: string[] = [];

            for (const line of lines) {
              if (!line || line.startsWith(":")) continue;
              if (line.startsWith("data:")) {
                dataLines.push(line.slice(5).trimStart());
              }
            }

            const dataStr = dataLines.join("\n").trim();

            if (!dataStr) continue;
            if (dataStr === "[DONE]") {
              done = true;
              break;
            }

            try {
              const payload: StreamPayload = JSON.parse(dataStr);

              if (payload.error) {
                const idx = inProgressIndexRef.current;
                const alertText = buildAlertText(payload.error);
                const summary = buildErrorSummary(payload.error);

                if (idx != null) {
                  setMessages((prev) => {
                    const arr = [...prev];
                    const current = arr[idx];
                    if (!current || current.role !== "assistant") return prev;
                    arr[idx] = {
                      ...current,
                      kind: "alert",
                      content: alertText,
                    };
                    return arr;
                  });
                }

                setError(summary);
                done = true;
                await reader.cancel();
                break;
              }

              const deltaContent =
                typeof payload.delta === "string"
                  ? payload.delta
                  : payload.delta?.content;

              if (deltaContent) {
                const idx = inProgressIndexRef.current;
                if (idx != null) {
                  setMessages((prev) => {
                    const arr = [...prev];
                    const current = arr[idx];
                    if (!current || current.role !== "assistant") return prev;
                    arr[idx] = {
                      ...current,
                      content: (current.content || "") + deltaContent,
                    };
                    return arr;
                  });
                }
              }
            } catch (e) {
              console.warn("[SSE] JSON parse error:", e, "raw:", dataStr);
            }
          }
        }

        setLoading(false);
        abortRef.current = null;
        inProgressIndexRef.current = null;
      } catch (err) {
        if ((err as any)?.name === "AbortError") {
          // cancelado
        } else {
          const msg = err instanceof Error ? err.message : "Error en el stream";
          const idx = inProgressIndexRef.current;
          if (idx != null) {
            setMessages((prev) => {
              const arr = [...prev];
              const current = arr[idx];
              if (!current || current.role !== "assistant") return prev;
              arr[idx] = {
                ...current,
                kind: "alert",
                content: `⚠️ **No pudimos completar la respuesta**\n\n${msg}`,
              };
              return arr;
            });
          }
          setError(msg);
        }
        setLoading(false);
        abortRef.current = null;
        inProgressIndexRef.current = null;
      }
    },
    [options.index, options.llm_provider, options.sessionId], // 👈 AGREGAR
  );

  return { messages, loading, error, sendMessage, cancel, loadHistory };
}
